import { ICellRendererParams } from 'ag-grid-community';
import * as React from 'react';
import { IWatrRequest } from '@nkr/models/index';
import { WageAndTravelCategory, WageAndTravelStatus, WageAndTravelType, WageAndTravelViewType } from '@nkr/enums/index';
import { Badge, Label } from 'react-bootstrap';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { getRowTotalsForPendingHeader, pendingColumnShowsTransactionHeaderTotal } from './utils/watr-grid-totals.utils';
import {
    getWatrGridStatusDisplay,
    getWatrGridStatusTooltip,
    hasPendingTravelApprovalForWatr,
    INTL_POST_WORKUP_STATUS_DISPLAY,
    isIntlStandardDerReimburseTravel,
    isIntlStandardDerVerify
} from './advanced-reimbursement-status-buckets';
import { getVerifyTaxStepLabel } from './utils/verify-tax-step-label';

export interface IPendingRendererProps extends ICellRendererParams {
    isSuperUser: boolean,
    category: WageAndTravelCategory,
    filterCat: WageAndTravelCategory,
    onActionRequest: (id: number, type: WageAndTravelViewType) => void
}

export type PendingCellResolve =
    | { kind: 'action'; type: number; text: string; combined: boolean }
    | { kind: 'total'; display: string }
    | { kind: 'status'; display: string }
    | { kind: 'none' };

export function resolvePendingCell(props: IPendingRendererProps): PendingCellResolve {
    let data = props.data as IWatrRequest;

    let isReimburseTravel =
        props.isSuperUser
        && (
            isIntlStandardDerReimburseTravel(data, props.category)
            || (
                data.donationConfirmed
                && data.status == WageAndTravelStatus.Donated
                && (
                    props.category == WageAndTravelCategory.PendingPayment && data.achReimbursementType == WageAndTravelType.ReimburseTravelOnly
                    || props.category == WageAndTravelCategory.PendingWire && data.requestTravels.filter(x => x.status == WageAndTravelReimbursementRequestStatus.AdminApproved).length > 0
                )
            )
        );

    let isVerifyTravel =
        props.isSuperUser
        && hasPendingTravelApprovalForWatr(data)
        && (
            (data.donationConfirmed && props.category == WageAndTravelCategory.PendingDERVerify)
            || isIntlStandardDerVerify(data)
        );

    let isReimburseWages =
        props.isSuperUser
        && data.donationConfirmed
        && data.status > WageAndTravelStatus.PendingNkrVerifyAmount
        && data.status < WageAndTravelStatus.Denied
        && (
            props.category == WageAndTravelCategory.PendingPayment && data.achReimbursementType == WageAndTravelType.ReimburseWagesOnly
            || props.category == WageAndTravelCategory.PendingWire && data.requestWeeks
                .filter(x => x.status >= WageAndTravelReimbursementRequestStatus.AutoApproved && x.status <= WageAndTravelReimbursementRequestStatus.AdminApproved).length > 0
        );

    let isVerifyWages =
        props.isSuperUser
        && props.category == WageAndTravelCategory.PendingLWRVerify
        && data.status >= WageAndTravelStatus.PendingNkrVerifyTax
        && data.status <= WageAndTravelStatus.PendingNkrVerifyAmount;

    let isPendingWeekConfirmation =
        !props.isSuperUser
        && data.requestWeeks
            .filter(x => x.status == WageAndTravelReimbursementRequestStatus.PendingApproval
                && x.weekRequested > 3).length > 0;

    let isConfirmDonation =
        !props.isSuperUser
        && !data.donationConfirmed
        && data.status < WageAndTravelStatus.Denied
        && data.donorShieldId > 0
        && data.afterDonationDate;

    let type = -1;
    let text = '';

    if (isReimburseTravel) {
        type = WageAndTravelViewType.ReimburseTravel;
        text = 'Reimburse Expenses';
    }

    if (isVerifyTravel) {
        type = WageAndTravelViewType.VerifyTravel;
        text = 'Verify Expenses';
    }

    if (isReimburseWages) {
        type = WageAndTravelViewType.ReimburseWage;
        text = 'Reimburse Wages';
    }

    if (isVerifyWages) {
        type = WageAndTravelViewType.VerifyWage;
        if (data.status === WageAndTravelStatus.PendingNkrVerifyTax) {
            text = getVerifyTaxStepLabel(data);
        } else if (data.status === WageAndTravelStatus.PendingNkrVerifyStub) {
            text = 'Verify Income';
        } else if (data.status === WageAndTravelStatus.PendingNkrVerifyAmount) {
            text = 'Verify Amount';
        } else {
            text = 'Verify Wages';
        }
    }

    if (isPendingWeekConfirmation) {
        type = WageAndTravelViewType.ApproveWeekReimbursements;
        text = 'Approve Weeks';
    }

    if (isConfirmDonation) {
        type = WageAndTravelViewType.ConfirmDonation;
        text = 'Confirm Donation';
    }

    if ((isReimburseTravel || isVerifyTravel) && (isReimburseWages || isVerifyWages)) {
        return { kind: 'action', type, text: 'Wage & Expenses', combined: true };
    }

    if (type != -1) {
        return { kind: 'action', type, text, combined: false };
    }

    if (pendingColumnShowsTransactionHeaderTotal(props.category, props.isSuperUser)) {
        const { transactionTotal } = getRowTotalsForPendingHeader(data, props.category);
        const display = transactionTotal === 0
            ? '--'
            : `$${Math.round(transactionTotal).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        return { kind: 'total', display };
    }

    const statusDisplay = getWatrGridStatusDisplay(data, false);
    if (statusDisplay) {
        return { kind: 'status', display: statusDisplay };
    }

    return { kind: 'none' };
}

export function renderPendingActionButton(
    props: IPendingRendererProps,
    resolved: Extract<PendingCellResolve, { kind: 'action' }>
): JSX.Element {
    let data = props.data as IWatrRequest;
    const onClick = (e: React.MouseEvent<any>) => {
        e.stopPropagation();
        props.onActionRequest(data.id, resolved.type);
    };

    if (resolved.combined) {
        return <Badge onClick={onClick}>{resolved.text}</Badge>;
    }

    return (
        <a className='btn btn-warning btn-xs' onClick={onClick}>
            {resolved.text}
        </a>
    );
}

export function renderPendingResolved(
    props: IPendingRendererProps,
    resolved: PendingCellResolve
): JSX.Element | null {
    let data = props.data as IWatrRequest;

    if (resolved.kind === 'action') {
        const stopAndAct = (e: { stopPropagation(): void }) => {
            e.stopPropagation();
            props.onActionRequest(data.id, resolved.type);
        };

        if (resolved.combined) {
            return <Badge onClick={stopAndAct}>{resolved.text}</Badge>;
        }

        return (
            <a className='btn btn-warning btn-xs' onClick={stopAndAct}>
                {resolved.text}
            </a>
        );
    }

    if (resolved.kind === 'total') {
        return (
            <span aria-label={resolved.display === '--' ? 'No pending transaction total' : `Pending transaction total ${resolved.display}`}>
                {resolved.display}
            </span>
        );
    }

    if (resolved.kind === 'status') {
        const isPostWorkup = resolved.display === INTL_POST_WORKUP_STATUS_DISPLAY;
        const labelStyle = data.status == WageAndTravelStatus.Donated ? 'success' : isPostWorkup ? 'info' : 'warning';
        const title = getWatrGridStatusTooltip(data, resolved.display);
        return (
            <Label bsStyle={labelStyle} style={{ fontSize: '100%', whiteSpace: 'nowrap' }} title={title}>
                {resolved.display}
            </Label>
        );
    }

    return null;
}

const PendingRenderer = (props: IPendingRendererProps) => {
    const resolved = resolvePendingCell(props);
    if (resolved.kind === 'action' || resolved.kind === 'none') {
        return null;
    }
    return renderPendingResolved(props, resolved);
};

export default PendingRenderer;
